import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '@yuan-math/config';
import { Mascot, Math } from '@yuan-math/ui';
import { registry } from '@yuan-math/content';

/**
 * The public homepage.
 *
 * Still deliberately short (§19): a headline, two buttons, the climb, and
 * proof that the mathematics is real. No carousel, no testimonials, no
 * countdown. What it does now do is *look* like the thing it is selling —
 * Sigma is here on the first screen because Sigma is who reacts to a child's
 * first answer, and the six stages are drawn as steps of one climb rather than
 * set as six paragraphs.
 */
export function HomePage(): JSX.Element {
  const stages = [
    { label: 'Kindergarten', detail: 'Counting and quantity' },
    { label: 'Elementary', detail: 'Fractions, decimals, area' },
    { label: 'Middle School', detail: 'Ratios, integers, equations' },
    { label: 'High School', detail: 'Functions, trigonometry, proof' },
    { label: 'Calculus', detail: 'Limits, derivatives, integrals' },
    { label: 'University', detail: 'Linear algebra, analysis, algebra' },
  ];

  const available = registry.availableConcepts().length;
  const total = registry.concepts.length;

  return (
    <>
      <section className="hero">
        <div className="hero__inner">
          <div className="hero__greeting">
            <Mascot mood="idle" size={64} />
            <p className="hero__eyebrow">{BRAND.tagline}</p>
          </div>
          <h1 className="hero__headline">{BRAND.heroHeadline}</h1>
          <p className="hero__subheadline">{BRAND.heroSubheadline}</p>

          <div className="hero__actions">
            <Link to="/start" className="btn btn--primary btn--lg">
              Start learning
            </Link>
            <Link to="/map" className="btn btn--secondary btn--lg">
              Explore the Math Map
            </Link>
          </div>

          <p className="hero__note">
            Free to start. No card, no trial clock, and nothing collected about a child beyond their own work.
          </p>

          <p className="hero__byline">Made by Ethan Yuan</p>
        </div>

        <div className="hero__figure" aria-hidden="true">
          <div className="hero__card">
            <p className="hero__card-label">A lesson, not a worksheet</p>
            <Math latex="\frac{2}{3} \times \frac{3}{4} = \frac{2 \times 3}{3 \times 4} = \frac{6}{12} = \frac{1}{2}" display />
            <p className="hero__card-note">
              Two thirds <em>of</em> three quarters. Multiplying can make a number smaller — and here is why.
            </p>
          </div>
        </div>
      </section>

      <section className="journey">
        <h2 className="journey__title">One continuous climb</h2>
        <p className="journey__subtitle">
          Not a stack of separate school years — a single connected subject you can enter anywhere.
        </p>
        <ol className="journey__list">
          {stages.map((stage, index) => (
            <li key={stage.label} className="journey__item">
              <span className="journey__index">{index + 1}</span>
              <span className="journey__label">{stage.label}</span>
              <span className="journey__detail">{stage.detail}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="principles">
        <div className="principles__grid">
          <article className="principle">
            <h3>Mastery, not completion</h3>
            <p>
              Nobody here tells you that you finished Grade 5. We tell you which concepts you have mastered, which
              are fading, and what is genuinely next — and we measure understanding, accuracy, retention and
              application separately, because they are different things.
            </p>
          </article>
          <article className="principle">
            <h3>Mathematics is a graph, not a ladder</h3>
            <p>
              Every concept knows what it depends on. When you struggle with adding fractions, the platform looks{' '}
              <em>underneath</em> the struggle and finds the common denominators you never quite settled — instead
              of handing you twenty more of the same problem.
            </p>
          </article>
          <article className="principle">
            <h3>Wrong answers are information</h3>
            <p>
              Answer <Math latex="\tfrac{1}{2} + \tfrac{1}{3}" /> with <Math latex="\tfrac{2}{5}" /> and you will not
              get a red cross. You will be told that you added the denominators, why that cannot be right, and which
              lesson repairs it.
            </p>
          </article>
          <article className="principle">
            <h3>Built to grow</h3>
            <p>
              {available} concepts are teachable today, inside a map of {total} that already reaches linear algebra,
              real analysis and olympiad problem solving. The architecture does not change when the content arrives.
            </p>
          </article>
        </div>
      </section>

      <section className="cta">
        <h2>Find out where you actually are.</h2>
        <p>
          A short adaptive assessment — no grade to declare, no sign-up needed to try it. It walks the concept graph
          the way a tutor would, and stops as soon as it knows.
        </p>
        <Link to="/diagnostic" className="btn btn--primary btn--lg">
          Take the placement test
        </Link>
      </section>
    </>
  );
}
